import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { defaultPagination } from 'src/constants/main';
import { ICheckoutDependencies } from 'src/mobx/checkoutStore/types';
import { modalStore } from 'src/mobx/modalStore';
import { IDeliveryTypeParams } from 'src/mobx/modalStore/types';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { IPagination } from 'src/types/main';
import { ICustomerAddress } from 'src/types/user';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';

interface IDeliveryState {
  [DELIVERY_TYPE.DELIVERY]: {
    location: ICheckoutDependencies['location'];
    branch: IBranch | undefined;
  };
  [DELIVERY_TYPE.PICKUP]: { branch: IBranch | undefined };
  [DELIVERY_TYPE.DINE_IN]: { branch: IBranch | undefined };
}

const defaultDeliveryState: IDeliveryState = {
  [DELIVERY_TYPE.PICKUP]: {
    branch: undefined,
  },
  [DELIVERY_TYPE.DINE_IN]: {
    branch: undefined,
  },
  [DELIVERY_TYPE.DELIVERY]: {
    branch: undefined,
    location: undefined,
  },
};

export default class DeliveryTypeStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable loading: boolean = false;

  @observable deliveryType: DELIVERY_TYPE = DELIVERY_TYPE.PICKUP;
  @observable selectedDeliveryState: IDeliveryState = defaultDeliveryState;

  @observable addressesPagination: IPagination<ICustomerAddress> = defaultPagination;
  @observable addressesPaginationPages: number[] = [];

  @observable isEditableOrder: boolean = false;

  /**
   * Method of initial setting of delivery parameters.
   *
   * @param {IDeliveryTypeParams} props
   */
  @action init(props: IDeliveryTypeParams) {
    this.isEditableOrder = Boolean(props.editable);

    this.selectedDeliveryState = {
      [DELIVERY_TYPE.DELIVERY]: {
        location: props.location,
        branch: props.delivery_type === DELIVERY_TYPE.DELIVERY ? props.branch : undefined,
      },
      [DELIVERY_TYPE.PICKUP]: {
        branch: props.delivery_type === DELIVERY_TYPE.PICKUP ? props.branch : undefined,
      },
      [DELIVERY_TYPE.DINE_IN]: {
        branch: props.delivery_type === DELIVERY_TYPE.DINE_IN ? props.branch : undefined,
      },
    };

    if (props.delivery_type) {
      this.deliveryType = props.delivery_type;
    }
  }

  @action fetchAddresses = async (page: number = this.addressesPagination.current_page + 1) => {
    try {
      // reset, if this is first request
      if (page === 1) {
        this.addressesPaginationPages = [];
        this.addressesPagination = defaultPagination;
      }

      this.addressesPagination = { ...this.addressesPagination, loading: true };

      const response = await client.get(`/v3/users/${String(modalStore.deliveryType?.customer)}/locations`, {
        params: { is_delete: 0, page, customers: 1 },
      });

      this.addressesPagination = { ...response, data: [...this.addressesPagination.data, ...response.data] };
      this.addressesPaginationPages = [...this.addressesPaginationPages, page];
    } catch (error: any) {
      this.addressesPagination = { ...this.addressesPagination, loading: false };
      logger.error({ error, place: 'DeliveryTypeStore fetchAddresses' });
    }
  };

  /**
   * Method for getting data (address, area_id and etc) about selected place, application of this place.
   *
   * @param {number} lat - latitude coordinate
   * @param {number} lng - longitude coordinate
   * @param {number} [customer_location_id]
   * @returns
   */
  @action getDeliveryDependencies = async (lat: number, lng: number, address_id?: number) => {
    try {
      if (this.deliveryType !== DELIVERY_TYPE.DELIVERY) return;

      this.loading = true;

      let address: string = this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].location?.address || '';

      if (!this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].location) {
        this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].location = {
          lat,
          lng,
          area_id: undefined,
          address,
          address_id,
        };
      }

      this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].branch = undefined;

      const cancelSearchId = `search`;
      if (this.cancelStack[cancelSearchId]) {
        this.cancelStack[cancelSearchId]('Cancelled');
      }

      const response = await client.get('/v3/branches/search', {
        params: { latitude: lat, longitude: lng, delivery_type: DELIVERY_TYPE.DELIVERY },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelSearchId] = c)),
      });

      if (response.address) address = response.address;

      if (!response.branch) {
        this.selectPlaceForOrder(undefined, { lat, lng, address, address_id }, this.deliveryType);
        this.loading = false;
        return;
      }

      this.selectPlaceForOrder(response.branch, { lat, lng, address, address_id, area_id: response.area.id }, DELIVERY_TYPE.DELIVERY);

      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'DeliveryTypeStore getDeliveryDependencies' });
    }
  };

  /**
   * Method for changing place of order.
   *
   * @param {IBranch} [branch] - branch for delivery
   * @param {ICheckoutDependencies['location']} location - location for delivery
   * @param {DELIVERY_TYPE} deliveryType - delivery type (delivery, pickup or dine-in)
   */
  @action selectPlaceForOrder = (branch: IBranch | undefined, location: ICheckoutDependencies['location'], deliveryType: DELIVERY_TYPE) => {
    this.selectedDeliveryState = {
      ...this.selectedDeliveryState,
      [deliveryType]: { ...this.selectedDeliveryState[deliveryType], branch },
    };

    if (deliveryType === DELIVERY_TYPE.DELIVERY) {
      this.selectedDeliveryState[deliveryType].location = location;
    }
  };

  @action selectDeliveryType = (deliveryType: DELIVERY_TYPE) => {
    this.deliveryType = deliveryType;
  };

  /**
   * Checks for branch coverage by coordinates.
   *
   * @param {number} latitude
   * @param {number} longitude
   * @returns Response contains a branch that has coverage of given coordinates.
   */
  @action checkAddress = async (latitude: number, longitude: number) => {
    try {
      this.loading = true;
      const response = await client.get('/v3/branches/search', {
        params: { latitude, longitude, delivery_type: DELIVERY_TYPE.DELIVERY },
      });
      this.loading = false;
      return response;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DeliveryTypeStore checkAddress' });
    }
  };

  /**
   * Method for getting address by coordinates.
   */
  @action defineLocationDeliveryAddress = async () => {
    try {
      const { location } = this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY];
      this.loading = true;

      const { address } = await client.get('v3/delivery/address', {
        params: { latitude: location?.lat, longitude: location?.lng },
      });

      this.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].location!.address = address;
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'DeliveryTypeStore defineLocationDeliveryAddress' });
    }
  };

  /**
   * Applies the selected delivery settings.
   * Applies changes only if order is editable.
   */
  @action submit = async () => {
    if (this.isEditableOrder) {
      let location: ICheckoutDependencies['location'] | undefined = undefined;

      if (this.deliveryType === DELIVERY_TYPE.DELIVERY) {
        await this.defineLocationDeliveryAddress();
        location = this.selectedDeliveryState[this.deliveryType].location;
      }

      modalStore.deliveryType?.onChange(this.selectedDeliveryState[this.deliveryType].branch, location, this.deliveryType);
    }

    modalStore.closeDeliveryType();
  };
}

export const deliveryTypeStore = new DeliveryTypeStore();
